import React, { useEffect, useState, createElement } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Hero } from './components/Hero';
import { MarketingHero } from './components/MarketingHero';
import { ProductDemo } from './components/ProductDemo';
import { AICoworkers } from './components/AICoworkers';
import { StayInControl } from './components/StayInControl';
import { ToolComparison } from './components/ToolComparison';
import { HowItWorks } from './components/HowItWorks';
import { HowItWorksConnections } from './components/HowItWorksConnections';
import { AlwaysOn } from './components/AlwaysOn';
import { CapabilityGrid } from './components/CapabilityGrid';
import { QuickCapture } from './components/QuickCapture';
import { LivesInSlack } from './components/LivesInSlack';
import { ConnectedWorkflow } from './components/ConnectedWorkflow';
import { ComputerUse } from './components/ComputerUse';
import { Pricing } from './pages/Pricing';
import { WallOfLove } from './components/WallOfLove';
import { NarrativeSection } from './components/NarrativeSection';
import { MemorySection } from './components/MemorySection';
import { SecuritySection } from './components/SecuritySection';
import { PricingSection } from './components/PricingSection';
import { ClosingCTA } from './components/ClosingCTA';
import { Layout } from './components/Layout';
import { IntroSplash } from './components/IntroSplash';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { Contact } from './pages/Contact';
import { Careers } from './pages/Careers';
import { Enterprise } from './pages/Enterprise';
import { ClientManagement } from './pages/ClientManagement';
import { GrowthMarketing } from './pages/GrowthMarketing';
import { FoundersExecs } from './pages/FoundersExecs';
import { Signup } from './pages/Signup';
import { Blog } from './pages/Blog';
import { Research } from './pages/Research';
import { CaseStudies } from './pages/CaseStudies';
import { Changelog } from './pages/Changelog';
import { Security } from './pages/Security';
import { CompareChatGPT } from './pages/CompareChatGPT';
import { WatchGigaDemo } from './pages/WatchGigaDemo';
import { BookDemo } from './pages/BookDemo';
import { Dashboard } from './pages/Dashboard';
import { Bespoke } from './pages/Bespoke';
import { Home } from './pages/Home';
import { HomeArchitecture } from './pages/HomeArchitecture';
import { HomeAlternative } from './pages/HomeAlternative';
import { EcommerceHome } from './pages/EcommerceHome';
import { FutureHome } from './pages/FutureHome';
import { Agents } from './pages/Agents';
import { WorkspaceHome } from './pages/WorkspaceHome';
import { SphereDirections } from './pages/SphereDirections';
import { ChatTransition } from './pages/ChatTransition';
import { ReferralCapture } from './pages/ReferralCapture';
import { UseCasesConcepts } from './pages/UseCasesConcepts';
import { FontOptions } from './pages/FontOptions';
import { ClaudeFont } from './pages/ClaudeFont';
import { Integrations } from './pages/Integrations';
import { Consulting } from './pages/Consulting';
import { CopyModeProvider } from './components/copy/copyMode';
function ScrollToTop() {
  const {
    pathname,
    hash
  } = useLocation();
  useEffect(() => {
    if (hash) {
      // Defer so the target route has mounted before we scroll to its anchor.
      const id = hash.replace('#', '');
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
          return;
        }
        window.scrollTo(0, 0);
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}
function GoogleAnalytics() {
  useEffect(() => {
    // Inject the Google tag script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-2PE1STVTDF';
    document.head.appendChild(script1);
    // Inject the initialization script
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-2PE1STVTDF');
    `;
    document.head.appendChild(script2);
    return () => {
      // Cleanup if needed (usually not necessary for analytics, but good practice)
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);
  return null;
}
export function App() {
  return <CopyModeProvider data-id="element-0">
      <GoogleAnalytics data-id="element-1" />
      <ScrollToTop data-id="element-2" />
      {/* Global Prado-style film grain over the entire app */}
      <div className="grain-global" aria-hidden="true" data-id="element-3" />
      <Routes data-id="element-4">
        <Route path="/signup" element={<Signup data-id="element-6" />} data-id="element-5" />
        <Route path="/login" element={<Signup data-id="element-8" />} data-id="element-7" />
        {/* Waitlist referral capture: /r/<code> records the inbound referral
         and forwards into the waitlist flow with ?ref=<code>. */}
        <Route path="/r/:code" element={<ReferralCapture data-id="element-10" />} data-id="element-9" />
        <Route path="/dashboard" element={<Dashboard data-id="element-12" />} data-id="element-11" />
        {/* Internal review page: editorial directions for the sphere. */}
        <Route path="/sphere-directions" element={<SphereDirections data-id="element-14" />} data-id="element-13" />
        {/* Experimental: new card-morph hand-off from the integration sphere
         into the chat panel. Not linked from the marketing nav. */}
        <Route path="/chat-transition" element={<ChatTransition data-id="element-16" />} data-id="element-15" />
        {/* Internal review: 5 concepts for the use cases page */}
        <Route path="/use-cases-concepts" element={<UseCasesConcepts data-id="element-18" />} data-id="element-17" />
        {/* Internal review: headline font candidates shown in context */}
        <Route path="/font-options" element={<FontOptions data-id="element-20" />} data-id="element-19" />
        {/* Internal review: trying Claude's font on the home hero in context */}
        <Route path="/claude-font" element={<ClaudeFont data-id="element-22" />} data-id="element-21" />

        <Route path="/" element={<Layout data-id="element-24" />} data-id="element-23">
          {/* New home screen, built on the Tasklet-style hero. */}
          <Route index element={<Home data-id="element-26" />} data-id="element-25" />
          {/* Preview of the new architecture diagram section */}
          <Route path="architecture" element={<HomeArchitecture data-id="element-28" />} data-id="element-27" />
          {/* Alternative home screen with static hero and recaps feed */}
          <Route path="home-alt" element={<HomeAlternative data-id="element-30" />} data-id="element-29" />
          {/* E-commerce / DTC ICP-positioned home variant. */}
          <Route path="ecommerce" element={<EcommerceHome data-id="element-32" />} data-id="element-31" />
          {/* The previous Giga homepage, preserved at /future. */}
          <Route path="future" element={<FutureHome data-id="element-34" />} data-id="element-33" />
          {/* Task-focused agents showcase. */}
          <Route path="agents" element={<Agents data-id="element-36" />} data-id="element-35" />
          {/* Exploration: home page with an animated Slack-workspace hero. */}
          <Route path="workspace" element={<WorkspaceHome data-id="element-38" />} data-id="element-37" />
          <Route path="terms" element={<Terms data-id="element-40" />} data-id="element-39" />
          <Route path="privacy" element={<Privacy data-id="element-42" />} data-id="element-41" />
          <Route path="contact" element={<Contact data-id="element-44" />} data-id="element-43" />
          <Route path="careers" element={<Careers data-id="element-46" />} data-id="element-45" />

          <Route path="enterprise" element={<Enterprise data-id="element-48" />} data-id="element-47" />
          <Route path="bespoke" element={<Bespoke data-id="element-50" />} data-id="element-49" />
          <Route path="consulting" element={<Consulting data-id="element-52" />} data-id="element-51" />
          <Route path="pricing" element={<Pricing data-id="element-54" />} data-id="element-53" />
          <Route path="use-cases/founders-execs" element={<FoundersExecs data-id="element-56" />} data-id="element-55" />
          <Route path="use-cases/client-management" element={<ClientManagement data-id="element-58" />} data-id="element-57" />
          
          <Route path="use-cases/growth-marketing" element={<GrowthMarketing data-id="element-60" />} data-id="element-59" />
          
          <Route path="blog" element={<Blog data-id="element-62" />} data-id="element-61" />
          <Route path="research" element={<Research data-id="element-64" />} data-id="element-63" />
          <Route path="case-studies" element={<CaseStudies data-id="element-66" />} data-id="element-65" />
          <Route path="changelog" element={<Changelog data-id="element-68" />} data-id="element-67" />
          <Route path="security" element={<Security data-id="element-70" />} data-id="element-69" />
          <Route path="integrations" element={<Integrations data-id="element-72" />} data-id="element-71" />
          <Route path="compare/chatgpt" element={<CompareChatGPT data-id="element-74" />} data-id="element-73" />
          <Route path="demo" element={<WatchGigaDemo data-id="element-76" />} data-id="element-75" />
          <Route path="demo/book" element={<BookDemo data-id="element-78" />} data-id="element-77" />
        </Route>
      </Routes>
    </CopyModeProvider>;
}